import { createElement, useState } from 'react';

import {
  Box,
  Checkbox,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { ControlPosition, MapControl } from '@vis.gl/react-google-maps';

import {
  IcClose,
  IcDeadAnimal,
  IcDebris,
  IcDownPole,
  IcDownWires,
  IcFireHydrant,
  IcFlooding,
  IcLimitedVisibility,
  IcLocationMarker,
  IcOpenManhole,
  IcPotHole,
  IcRoadBlock,
  IcRoadWork,
  IcStacks,
} from '@/assets/images';
import { getMarkerTypeKey } from '@/constants';
import { MapMarkerTypes } from '@/hooks/useMapMarkerTypes';
import { useMarkerTypes } from '@/hooks/useMarkerTypes';

import { useStyles } from './sharedMapStyles';

// Icon mapping for marker types
const MARKER_TYPE_ICONS: Record<string, any> = {
  'Fire Hydrant': IcFireHydrant,
  Pothole: IcPotHole,
  'Road Block': IcRoadBlock,
  Flooding: IcFlooding,
  'Limited Visibility': IcLimitedVisibility,
  'Dead Animal': IcDeadAnimal,
  'Down Wires': IcDownWires,
  'Down Pole': IcDownPole,
  Debris: IcDebris,
  'Open Manhole': IcOpenManhole,
  'Road Work': IcRoadWork,
};

// Function to get marker type color (same as in FireHydrantMap)
const getMarkerTypeColor = (type: string, markerTypes: any[]): string => {
  if (typeof type === 'string') {
    const markerType = markerTypes.find((mt) => mt.name === type);
    if (markerType && markerType.color) {
      return markerType.color;
    }

    // Fallback to a deterministic color based on the type name
    const fallbackColors = [
      '#FF6B6B',
      '#4ECDC4',
      '#45B7D1',
      '#96CEB4',
      '#FFEAA7',
      '#DDA0DD',
      '#98D8C8',
      '#F7DC6F',
      '#BB8FCE',
      '#85C1E9',
    ];

    let hash = 0;
    for (let i = 0; i < type.length; i++) {
      hash = type.charCodeAt(i) + ((hash << 5) - hash);
    }
    const colorIndex = Math.abs(hash) % fallbackColors.length;
    return fallbackColors[colorIndex];
  }
  return '#FF6B6B'; // Default red color
};

// Keep this for backward compatibility
export const MAP_MARKERS_TYPES = [
  {
    key: 'fireHydrant',
    label: 'Fire Hydrant',
    value: 'FIRE_HYDRANT',
    icon: IcFireHydrant,
  },
  {
    key: 'pothole',
    label: 'Pothole',
    value: 'POTHOLE',
    icon: IcPotHole,
  },
  {
    key: 'roadBlock',
    label: 'Road Block',
    value: 'ROAD_BLOCK',
    icon: IcRoadBlock,
  },
  {
    key: 'flooding',
    label: 'Flooding',
    value: 'FLOODING',
    icon: IcFlooding,
  },
  {
    key: 'limitedVisibility',
    label: 'Limited Visibility',
    value: 'LIMITED_VISIBILITY',
    icon: IcLimitedVisibility,
  },
  {
    key: 'deadAnimal',
    label: 'Dead Animal',
    value: 'DEAD_ANIMAL',
    icon: IcDeadAnimal,
  },
  {
    key: 'downWires',
    label: 'Down Wires',
    value: 'DOWN_WIRES',
    icon: IcDownWires,
  },
  {
    key: 'downPole',
    label: 'Down Pole',
    value: 'DOWN_POLE',
    icon: IcDownPole,
  },
  {
    key: 'debris',
    label: 'Debris',
    value: 'DEBRIS',
    icon: IcDebris,
  },
  {
    key: 'openManhole',
    label: 'Open Manhole',
    value: 'OPEN_MANHOLE',
    icon: IcOpenManhole,
  },
  {
    key: 'roadWork',
    label: 'Road Work',
    value: 'ROAD_WORK',
    icon: IcRoadWork,
  },
];

export type MapMarkerControlProps = {
  isExpanded?: boolean;
  position?: ControlPosition;
  onExpand?: () => void;
  onCondense?: () => void;
  selectedTypes: MapMarkerTypes;
  handleCheckboxChange: (key: keyof MapMarkerTypes) => void;
};

export const MapMarkerControl = ({
  position = ControlPosition.LEFT_BOTTOM,
  selectedTypes,
  handleCheckboxChange,
}: MapMarkerControlProps) => {
  const classes = useStyles();
  const { enabledMarkerTypes, markerTypes } = useMarkerTypes();

  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const renderButton = () => {
    return (
      <IconButton
        className={`${classes.controllButton} `}
        onClick={handleClick}
        id="map-marker-control-button"
        aria-controls={open ? 'map-marker-control-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
      >
        <IcStacks />
      </IconButton>
    );
  };

  return (
    <MapControl position={position}>
      <Box sx={{ ml: 1, mb: 1 }}>
        <div>{renderButton()}</div>
      </Box>
      <Menu
        sx={{ ml: 1, my: 0 }}
        id="map-marker-control-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          'aria-labelledby': 'map-marker-control-button',
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography fontWeight={500} fontSize={14} sx={{ ml: 1 }}>
            Map Layers
          </Typography>
          <IconButton onClick={handleClose}>
            <IcClose color={theme.palette.error.main} />
          </IconButton>
        </Stack>
        {enabledMarkerTypes.map((markerType) => {
          const key = getMarkerTypeKey(markerType.name);

          // Use predefined icon if available, otherwise use IcLocationMarker with custom color
          let icon = MARKER_TYPE_ICONS[markerType.name];
          let iconProps = {};

          if (!icon) {
            icon = IcLocationMarker;
            iconProps = {
              color: getMarkerTypeColor(markerType.name, markerTypes),
            };
          }

          return (
            <MenuItem
              key={markerType._id}
              sx={{ px: 1, py: 0 }}
              onClick={() => handleCheckboxChange(key as keyof MapMarkerTypes)}
            >
              <Stack
                direction="row"
                width="100%"
                height="35px"
                alignItems="center"
                justifyContent="flex-start"
              >
                {createElement(icon, {
                  ...iconProps,
                  style: {
                    height: 24,
                    width: 24,
                    marginRight: 8,
                    border: `2px solid`,
                    borderColor: selectedTypes[key as keyof MapMarkerTypes]
                      ? `${theme.palette.error.main}`
                      : 'transparent',
                    borderRadius: 4,
                    ...iconProps,
                  },
                })}
                <Typography
                  fontSize={14}
                  fontWeight={500}
                  color={
                    selectedTypes[key as keyof MapMarkerTypes]
                      ? theme.palette.text.primary
                      : theme.palette.text.disabled
                  }
                >
                  {markerType.name}
                </Typography>
              </Stack>
            </MenuItem>
          );
        })}
        <MenuItem
          sx={{ px: 1, py: 0 }}
          onClick={() => handleCheckboxChange('showInactive')}
        >
          <Stack
            direction="row"
            width="100%"
            height="35px"
            alignItems="center"
            justifyContent="flex-start"
          >
            <Checkbox
              size="small"
              checked={selectedTypes.showInactive}
              sx={{ marginRight: 1 }}
            />
            <Typography
              fontSize={14}
              fontWeight={500}
              color={
                selectedTypes.showInactive
                  ? theme.palette.text.primary
                  : theme.palette.text.disabled
              }
            >
              Show inactive
            </Typography>
          </Stack>
        </MenuItem>
      </Menu>
    </MapControl>
  );
};
